import {
  type Invoice,
  ServiceTypeEnum,
  type Statement,
} from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { queryOptionsGetInvoicesPaginated } from '../../../../api/payments/invoiceApi';
import { queryOptionsGetStatementsPaginated } from '../../../../api/payments/statementApi';
import { queryOptionsGetWorkspace } from '../../../../api/workspaces/workspaceApi';
import { AppSearchInput } from '../../../../components/AppSearchInput';
import { ServiceBadge } from '../../../../components/Badges/ServiceBadge';
import CurrencyFormatter from '../../../../components/CurrencyFormatter';
import DateFormatter from '../../../../components/DateFormatter';
import { MSR3Empty } from '../../../../components/MSR3Empty';
import { AppDataTable } from '../../../../components/data-table';
import { SEARCH_KEYS } from '../../../../constant/SearchKeys';
import {
  msr3StatementSearchPlaceholder,
  royaltiesAnzTitleSearchPlaceholder,
} from '../../../../constant/paymentsSearchPlaceholders';
import { useResetPageOnSearchChange } from '../../../../hooks/useResetPageOnSearchChange';
import { useRoyaltyFileDownload } from '../../../../hooks/useRoyaltyFileDownload';
import { useSetSearchParam } from '../../../../hooks/useSetSearchParam';
import { STATEMENT_FILE_DOWNLOADS } from '../../../../utils/api/statementFileDownloads';
import {
  formatSeasonNo,
  workspaceTypeIsAgent,
} from '../../../../utils/helpers/common';
import {
  type StatementFileFlags,
  getStatementFiles,
} from '../../../../utils/helpers/statementFiles';
import { StatementDownloadButtons } from '../../Payments/StatementDownloadButtons';
import {
  dateSortKey,
  defaultTabSortBy,
  tabInvoicesParams,
  tabStatementsParams,
  usesStatementsApi,
} from './statementFilterTabs';

const AMOUNT_SORT_KEY = 'amountNet';
const SEARCH_DEBOUNCE_MS = 400;

/**
 * `sortBy` when its primary field is one the active API can sort this table
 * on (its own date field, or Amount); otherwise that API's default sort.
 *
 * The URL `sortBy` key isn't namespaced, and it outlives the active tab, which
 * only lives in component state. So reloading (or coming Back) after sorting
 * Disbursements by date reopens on Royalties ANZ with `invoiceDate:…`, which
 * the Statements API doesn't reject. Every row ties on the missing field, so
 * rows come back in `_id` order rather than by date, with no sort arrow on the
 * Date header.
 */
const sortByForMode = (sortBy: null | string, isStatementsMode: boolean) => {
  const [field, direction] = sortBy?.split(',')[0].split(':') ?? [];
  const isSortable =
    (field === dateSortKey(isStatementsMode) || field === AMOUNT_SORT_KEY) &&
    (direction === 'asc' || direction === 'desc');

  return sortBy && isSortable ? sortBy : defaultTabSortBy(isStatementsMode);
};

/**
 * Row shape shared by both data sources so the table has a single set of
 * columns regardless of which API backs the active tab. `date` normalises
 * `Statement.statementDate` / `Invoice.invoiceDate` — the two APIs' only
 * structural difference relevant to this table.
 */
interface StatementRow extends StatementFileFlags {
  _id: string;
  amountNet: number;
  beneficiaryClientName?: string;
  beneficiarySeasonNo?: string;
  beneficiaryTitleName?: string;
  currency: string;
  date: string;
  /** `statementNo` / `invoiceNo`; names the downloaded file. */
  referenceNo?: string;
}

const toStatementRow = (statement: Statement): StatementRow => ({
  ...statement,
  date: statement.statementDate,
  referenceNo: statement.statementNo,
});

const toInvoiceRow = (invoice: Invoice): StatementRow => ({
  ...invoice,
  date: invoice.invoiceDate,
  referenceNo: invoice.invoiceNo,
});

interface IStatementsDataTableProps {
  /** Scopes both APIs' `beneficiaryClientId` filter to one client. */
  clientId?: string;
  /**
   * Hides the Client column even for an agent workspace. Pass this when the
   * page is already scoped to a single client (e.g. the Clients Summary
   * "Statements" card) so every row repeating the same client name would be
   * redundant — the same reasoning as `hideTitleColumn` (ADR-CLP-002).
   */
  hideClientColumn?: boolean;
  /**
   * Hides the Service column. Independent of `hideTitleColumn` — e.g. the
   * Title Statements card hides Title but keeps Service, since each row's
   * service badge is still meaningful once a tab already scopes the whole
   * table to a single service.
   */
  hideServiceColumn?: boolean;
  /**
   * Hides the Title and Season columns. Pass this when the page is already
   * scoped to a single title, so every row repeating the same title name would
   * be redundant. A serial header is the exception: its rows are one per season.
   */
  hideTitleColumn?: boolean;
  isStaffView?: boolean;
  /**
   * URL query param this table reads/writes its page number from. Owned by the
   * caller so each page gets a distinct key and paginating one page never
   * bleeds into another's page state.
   */
  paginationKey: string;
  /**
   * Overrides the default search placeholder (the sidebar Payments pages' copy
   * for the active API). Pass this when the table hides a column that copy
   * names, so the hint only offers fields a row actually shows.
   */
  searchPlaceholder?: string;
  /**
   * Scopes statements to a serial header (series root) rather than to a single
   * title. Statements mode only: the Invoices API has no serial-level filter.
   */
  serialTitleId?: string;
  /**
   * Service types to display in this statement table. Also determines which
   * backend API this table queries: a set made up entirely of Royalties
   * ANZ/World uses the Statements API; anything else (Disbursements,
   * Residuals, or no `serviceTypes` at all) uses the Invoices API.
   */
  serviceTypes?: ServiceTypeEnum[];
  /**
   * When true (and not a staff view), an empty result renders the rich
   * `EmptyValueErrorLayout` instead of the plain in-table empty message.
   */
  showRichEmptyState?: boolean;
  /**
   * Also renders pagination controls inline with the search box, in addition
   * to the footer pagination. Off by default.
   */
  showTopPagination?: boolean;
  titleId?: string;
  workspaceId?: string;
}

/**
 * Reusable statements table for the MSR3 Payments pages using the new AppDataTable
 * component. Renders finalised statements/paid invoices for a workspace,
 * optionally filtered by one or more service types — see `serviceTypes` above
 * for which backend API a given tab queries.
 *
 * Whichever API is active, its React Query keys on the full param set — so
 * each `(workspaceId, serviceType, page, sortBy)` combination is its own
 * entry and one Payments page can never paint another's rows.
 */
export const StatementsDataTable = ({
  clientId,
  hideClientColumn = false,
  hideServiceColumn = false,
  hideTitleColumn = false,
  isStaffView = false,
  paginationKey,
  searchPlaceholder,
  serialTitleId,
  serviceTypes,
  showRichEmptyState,
  showTopPagination,
  titleId,
  workspaceId,
}: IStatementsDataTableProps) => {
  // Per the "Invoices & Statements Definitions" Confluence doc and APP-2090:
  // Royalties ANZ/World individual statements are backed by the
  // Statements API. Disbursements (a single tab covering both the CAM and DAS
  // service types) and Residuals stay on the Invoices API used by the rest of
  // Payments — there's no statement record for those service types, and no
  // immediate plan to create one, so the Invoices API is their permanent home
  // rather than a stopgap.
  const isStatementsMode = usesStatementsApi(serviceTypes);
  const defaultSortBy = defaultTabSortBy(isStatementsMode);

  const [searchParams] = useSearchParams();
  const setParam = useSetSearchParam();

  // Seed page/sort from the URL; AppDataTable keeps them in sync via onPageRequest.
  const [page, setPage] = useState(
    () => Number(searchParams.get(paginationKey)) || 1,
  );
  // The sort is kept with the API it was chosen under. A tab switch that
  // crosses between the Statements-backed Royalty tabs and the Invoices-backed
  // Disbursements/Residuals tabs resets it to the new API's default, since the
  // two APIs sort on differently-named date fields (`statementDate` vs
  // `invoiceDate`). The reset happens during render rather than in an effect,
  // so the crossing render never requests one API with the other's field.
  const [sortState, setSortState] = useState(() => ({
    isStatementsMode,
    sortBy: sortByForMode(searchParams.get('sortBy'), isStatementsMode),
  }));
  const isSortForThisMode = sortState.isStatementsMode === isStatementsMode;
  const sortBy = isSortForThisMode ? sortState.sortBy : defaultSortBy;

  if (!isSortForThisMode) setSortState({ isStatementsMode, sortBy });

  // `search` is read straight from the URL (the committed term) so it and
  // `useResetPageOnSearchChange` below only change one render after the URL
  // itself is updated — staggering the two `setSearchParams` calls across
  // commits instead of racing them in the same one. Local `searchInput`
  // state keeps typing snappy; the debounced value is what gets committed to
  // the URL. Mirrors `TitlesV3`'s search field, including the deliberate
  // absence of an effect resyncing `searchInput` from `search` — that would
  // fight the debounce round-trip, since the URL holds the trimmed value and
  // resyncing would stomp characters typed since the last commit.
  const search = searchParams.get(SEARCH_KEYS.STATEMENT_SEARCH) || '';
  const [searchInput, setSearchInput] = useState(search);
  const [debouncedSearch] = useDebounceValue(
    searchInput.trim(),
    SEARCH_DEBOUNCE_MS,
  );

  useEffect(() => {
    if (debouncedSearch !== search) {
      setParam(SEARCH_KEYS.STATEMENT_SEARCH, debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Also reset to page 1 when the active service tab changes — this table
  // stays mounted across tab switches (only the `serviceTypes` prop changes),
  // so without this a page number from one tab could carry over to another
  // tab with fewer pages, showing an empty result.
  useResetPageOnSearchChange(
    `${search}|${serviceTypes?.join(',') ?? ''}`,
    paginationKey,
    setPage,
  );

  const { data: workspace } = useQuery(queryOptionsGetWorkspace(workspaceId));
  const isAgent = workspaceTypeIsAgent(workspace);

  // Finalised statements / paid invoices in scope (see `tabStatementsParams`,
  // which Client Summary's tab check also uses for each tab's first page).
  const scope = { clientId, serialTitleId, serviceTypes, titleId, workspaceId };
  const pageParams = { page, searchTerm: search, sortBy };

  const statementsQuery = useQuery(
    queryOptionsGetStatementsPaginated(
      isStatementsMode ? tabStatementsParams(scope, pageParams) : undefined,
    ),
  );

  const invoicesQuery = useQuery(
    queryOptionsGetInvoicesPaginated(
      isStatementsMode ? undefined : tabInvoicesParams(scope, pageParams),
    ),
  );

  const activeQuery = isStatementsMode ? statementsQuery : invoicesQuery;
  const rows: StatementRow[] = isStatementsMode
    ? (statementsQuery.data?.items ?? []).map(toStatementRow)
    : (invoicesQuery.data?.items ?? []).map(toInvoiceRow);
  const paginationMeta = activeQuery.data?.meta ?? null;

  // Both hooks are called unconditionally (rules of hooks); only the one
  // matching the active API is used below.
  const statementDownload = useRoyaltyFileDownload(
    STATEMENT_FILE_DOWNLOADS.statement,
  );
  const invoiceDownload = useRoyaltyFileDownload(
    STATEMENT_FILE_DOWNLOADS.invoice,
  );

  const handlePageRequest = (nextPage: number, nextSortBy = defaultSortBy) => {
    setPage(nextPage);
    setSortState({ isStatementsMode, sortBy: nextSortBy });
  };

  // Define columns for the AppDataTable
  const columns: ColumnDef<StatementRow>[] = [
    {
      accessorKey: 'date',
      cell: ({ row }) => <DateFormatter dateString={row.original.date} />,
      header: 'Date',
      meta: {
        sortKey: dateSortKey(isStatementsMode),
      },
    },
    ...(hideTitleColumn
      ? []
      : [
          {
            accessorKey: 'beneficiaryTitleName',
            cell: ({ row }: { row: { original: StatementRow } }) =>
              row.original.beneficiaryTitleName || 'Unknown Title',
            header: 'Title',
          } as ColumnDef<StatementRow>,
        ]),
    // Invoices carry no season, so the column only exists in statements mode.
    ...(!hideTitleColumn && isStatementsMode
      ? [
          {
            accessorKey: 'beneficiarySeasonNo',
            cell: ({ row }: { row: { original: StatementRow } }) => (
              <span className="whitespace-nowrap">
                {formatSeasonNo(row.original.beneficiarySeasonNo)}
              </span>
            ),
            header: 'Season',
          } as ColumnDef<StatementRow>,
        ]
      : []),
    ...(!hideClientColumn && workspaceTypeIsAgent(workspace)
      ? [
          {
            accessorKey: 'beneficiaryClientName',
            cell: ({ row }: { row: { original: StatementRow } }) =>
              row.original.beneficiaryClientName || 'Unknown Client',
            header: 'Client',
          } as ColumnDef<StatementRow>,
        ]
      : []),
    ...(hideServiceColumn
      ? []
      : [
          {
            accessorKey: 'serviceType',
            cell: ({ row }: { row: { original: StatementRow } }) => (
              <ServiceBadge serviceType={row.original.serviceType} />
            ),
            header: 'Service',
          } as ColumnDef<StatementRow>,
        ]),
    {
      accessorKey: 'amountNet',
      cell: ({ row }: { row: { original: StatementRow } }) => (
        <CurrencyFormatter amount={row.original.amountNet} />
      ),
      header: 'Amount',
      meta: { sortKey: AMOUNT_SORT_KEY },
    },
    {
      accessorKey: 'currency',
      cell: ({ row }: { row: { original: StatementRow } }) =>
        row.original.currency,
      header: 'Currency',
    },
    {
      accessorKey: 'download',
      cell: ({ row }: { row: { original: StatementRow } }) =>
        isStatementsMode ? (
          <StatementDownloadButtons
            download={statementDownload}
            files={getStatementFiles(row.original, 'statement')}
            row={row.original}
          />
        ) : (
          <StatementDownloadButtons
            download={invoiceDownload}
            files={getStatementFiles(row.original, 'invoice')}
            row={row.original}
          />
        ),
      enableSorting: false,
      header: () => <div className="text-center">Download</div>,
    },
  ];

  // Only short-circuit the whole table (incl. pagination) on the first page.
  // On later pages an empty result must still render the table so the user can
  // page back; otherwise a stale/too-high page number traps them on a dead end.
  // A no-result *search* must keep the table (and its search field) on screen
  // so the user can clear/retry — falling through to the in-table empty state.
  if (
    showRichEmptyState &&
    !isStaffView &&
    !search &&
    activeQuery.isSuccess &&
    rows.length === 0 &&
    page === 1
  ) {
    return <MSR3Empty {...MSR3Empty.Props.payment} />;
  }

  // Sidebar pages' Statements/Invoices API copy is design-approved, so reuse it
  // instead of bespoke strings, unless the caller overrides it.
  const placeholder =
    searchPlaceholder ??
    (isStatementsMode
      ? royaltiesAnzTitleSearchPlaceholder(isAgent)
      : msr3StatementSearchPlaceholder(isAgent));

  return (
    <AppDataTable
      id="statements-table"
      columns={columns}
      data={rows}
      emptyState={{
        description: '',
        icon: showRichEmptyState && !isStaffView ? 'folderOpen' : undefined,
        title: 'No Statements Found',
      }}
      filterElement={
        <AppSearchInput
          classes={{ wrapper: 'sm:w-64 md:w-96' }}
          isFetching={activeQuery.isFetching}
          placeholder={placeholder}
          value={searchInput}
          onChange={setSearchInput}
        />
      }
      isLoading={activeQuery.isPending}
      paginationKey={paginationKey}
      paginationMeta={paginationMeta}
      showTopPagination={showTopPagination}
      sortBy={sortBy}
      onPageRequest={handlePageRequest}
    />
  );
};
